import React, { useEffect, useMemo, useState } from "react";
import {
    BlockContent,
    CommandVariable,
    MessageButton,
    OrderInfoPayload,
    ProductInfo,
    TextBlockPayload,
} from "../../../../types/chat-message";
import { safePostMessage } from "../../../../utils";
import { getButtonStyle } from "@utils/buttonUtils";
import { PostMessageType } from "@constants/postMessage";
import "./OrderCard.scss";
import { useTranslation } from "react-i18next";
import type { ToastMessage } from '@hooks/useChat';

interface OrderCardProps {
  blockContents: BlockContent[];
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  onThinkingStop?: () => void;
  onScrollToBottom?: () => void;
  isLastMessage?: boolean;
  chatMessageTime?: string;
  isFromHistory?: boolean; // Flag to indicate if message is from history
  setToastMessage?: (toast: NonNullable<ToastMessage>) => void;
}

// Helper function to check if button should be hidden
const checkIsHidden = (commandVariables?: CommandVariable[]): boolean => {
  if (!commandVariables) return false;
  const hiddenVar = commandVariables.find((v) => v.name === "hidden_message");
  if (!hiddenVar) return false;
  return hiddenVar.value === "true";
};

// Helper function to check if a value is a valid number
const isValidNumber = (value: string | undefined): boolean => {
  if (!value) return false;
  const num = parseFloat(value.replace(/[^0-9.-]/g, ""));
  return !isNaN(num) && isFinite(num);
};

// Helper function to parse number safely
const parseNumber = (value: string | undefined): number => {
  if (!value) return NaN;
  return parseFloat(value.replace(/[^0-9.-]/g, ""));
};

// Helper function to format number with 2 decimal places
const formatNumber = (value: number): string => {
  return value.toLocaleString("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Process products with validation
const processProducts = (products: ProductInfo[]): ProductInfo[] => {
  if (products.length === 0) return products;

  return products.map((product) => {
    const quantityValid = isValidNumber(product.quantity);
    const priceValid = isValidNumber(product.price);
    const totalValid = isValidNumber(product.totalAmount);

    let processedQuantity = product.quantity;
    let processedPrice = product.price;
    let processedTotal = product.totalAmount;

    // Case: If totalAmount is invalid
    if (!totalValid && priceValid && quantityValid) {
      // Calculate total from price * quantity
      const qty = parseNumber(product.quantity);
      const price = parseNumber(product.price);
      processedTotal = formatNumber(qty * price);
    }
    // Case: If totalAmount is still invalid, try calculating from price and quantity
    else if (!totalValid && priceValid && !quantityValid) {
      // Calculate quantity from total / price (if we had total)
      // But if total is also invalid, we can't calculate
      // So we'll mark quantity as 1
      processedQuantity = "1";
      const qty = parseNumber(processedQuantity);
      const price = parseNumber(product.price);
      processedTotal = formatNumber(qty * price);
    }
    // Case: If quantity is invalid but price and total are valid
    else if (!quantityValid && priceValid && totalValid) {
      // Calculate quantity from total / price
      const total = parseNumber(product.totalAmount);
      const price = parseNumber(product.price);
      const qty = total / price;
      processedQuantity = formatNumber(Math.round(qty * 100) / 100);
    }
    // Case: If quantity is invalid and price is also invalid
    else if (!quantityValid && !priceValid) {
      // Default quantity to 1
      processedQuantity = "1";
    }

    // If price is invalid, we can't calculate anything, keep as is

    return {
      ...product,
      quantity: processedQuantity,
      price: processedPrice,
      totalAmount: processedTotal,
    };
  });
};

const OrderCard: React.FC<OrderCardProps> = ({
  blockContents,
  onButtonClick,
  onThinkingStop,
  onScrollToBottom,
  isLastMessage,
  chatMessageTime,
  isFromHistory,
  setToastMessage,
}) => {
  const { t } = useTranslation();

  // Scroll to bottom on mount
  useEffect(() => {
    onScrollToBottom?.();
  }, [onScrollToBottom]);

  const [selectedInvoice, setSelectedInvoice] = useState(false);

  // 1. Sort Blocks Dynamically by Order
  const sortedBlocks = [...blockContents].sort((a, b) => {
    const orderA = (a.payload as any).order ?? 0;
    const orderB = (b.payload as any).order ?? 0;
    return orderA - orderB;
  });

  const orderInfoBlock = blockContents.find((b) => b.type === "ORDER_INFO");
  const rawProducts: ProductInfo[] =
    (orderInfoBlock?.payload as OrderInfoPayload)?.products || [];

  // Process products with validation
  const products = useMemo(() => processProducts(rawProducts), [rawProducts]);

  // Find button block (also contains paymentMethod)
  const buttonBlock = blockContents.find(
    (b) =>
      b.type === "TEXT" &&
      (b.payload as TextBlockPayload).buttons &&
      (b.payload as TextBlockPayload).buttons!.length > 0,
  );
  const buttons = (buttonBlock?.payload as TextBlockPayload)?.buttons || [];
  const paymentMethod = (buttonBlock?.payload as TextBlockPayload)
    ?.paymentMethod;

  // Process Buttons
  const paymentButtons = buttons.filter((btn: MessageButton) =>
    btn.commandVariables?.some(
      (v: CommandVariable) => v.name === "payment_method",
    ),
  );

  const actionButtons = buttons.filter(
    (btn: MessageButton) =>
      !btn.commandVariables?.some(
        (v: CommandVariable) => v.name === "payment_method",
      ),
  );

  const defaultPaymentIndex = useMemo(() => {
    const idx = paymentButtons.findIndex((btn: MessageButton) =>
      btn.commandVariables?.some(
        (v: CommandVariable) => v.name === "is_choose" && v.value === "true",
      ),
    );
    return idx !== -1 ? idx : 0;
  }, [paymentButtons]);

  const [isActionTaken, setIsActionTaken] = useState(false);
  const [selectedPaymentIndex, setSelectedPaymentIndex] =
    useState(defaultPaymentIndex);

  useEffect(() => {
    setSelectedPaymentIndex(defaultPaymentIndex);
  }, [defaultPaymentIndex]);

  const activePaymentBtn =
    paymentButtons[selectedPaymentIndex] || paymentButtons[0];

  // Countdown logic
  // Get countdown value from active payment button
  const countdownVar = activePaymentBtn?.commandVariables?.find(
    (v: CommandVariable) => v.name === "count_down",
  );
  const initialCountdown = countdownVar
    ? parseInt(countdownVar.value, 10)
    : null;

  const [countdown, setCountdown] = useState<number | null>(null);

  // Initialize/Manage countdown
  useEffect(() => {
    if (initialCountdown !== null && !isActionTaken && !isFromHistory) {
      if (isLastMessage) {
        const msgTime = chatMessageTime
          ? new Date(chatMessageTime).getTime()
          : Date.now();
        const now = Date.now();
        const secondsPassed = Math.max(0, Math.floor((now - msgTime) / 1000));

        const remaining = initialCountdown - secondsPassed;
        setCountdown(Math.max(0, remaining));
      } else {
        setCountdown(null);
      }
    } else {
      setCountdown(null);
    }
  }, [isLastMessage, chatMessageTime, initialCountdown, isActionTaken, isFromHistory]);

  // Broadcast countdown active state to window so EmbedBox can intercept agent switch
  useEffect(() => {
    const isActive = countdown !== null && countdown > 0 && !isActionTaken;
    window.dispatchEvent(
      new CustomEvent("order-countdown-change", { detail: { isActive } }),
    );
    return () => {
      // When unmounted, signal countdown is gone
      window.dispatchEvent(
        new CustomEvent("order-countdown-change", {
          detail: { isActive: false },
        }),
      );
    };
  }, [countdown, isActionTaken]);

  // Huỷ countdown khi đóng chatbot
  useEffect(() => {
    const handleChatbotClose = () => {
      setCountdown(null);
      setIsActionTaken(true);
    };
    window.addEventListener('chatbot-close', handleChatbotClose);
    return () => window.removeEventListener('chatbot-close', handleChatbotClose);
  }, []);

  // Timer effect
  useEffect(() => {
    if (countdown === null) return;

    if (countdown <= 0) {
      if (!isActionTaken && isLastMessage && initialCountdown !== null) {
        const enterButton = buttons.find(
          (btn: MessageButton) => btn.shortCut === "ENTER",
        );
        if (enterButton) {
          handleButtonClick(enterButton.name, enterButton.id, enterButton);
        }
      }
      setCountdown(null);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => (prev !== null && prev > 1 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown, isActionTaken, isLastMessage, buttons, initialCountdown]);

  const handleButtonClick = (name: string, id: number, btn: MessageButton) => {
    if (isActionTaken || !isLastMessage) return;

    if (!btn.isHasPermission) {
      setToastMessage?.({
        text: t('common.noPermissions'),
        type: 'error',
      });
      return;
    }
    
    // Check for deeplink - only emit if button has no shortCut
    const deeplinkVar = btn.commandVariables?.find(
      (v) => v.name === "deeplink_web",
    );
    const isDeeplinkButton = deeplinkVar && !btn.shortCut;

    // Check if button should be hidden
    const isHidden = checkIsHidden(btn.commandVariables);

    // For deeplink buttons, only emit event without sending to socket
    if (isDeeplinkButton) {
      // Disable button and stop countdown
      setIsActionTaken(true);
      setCountdown(null);

      // Emit deeplink event
      setTimeout(() => {
        const rawUrl = deeplinkVar?.value || '';
        let routePath = rawUrl;
        try {
          const parsed = new URL(rawUrl);
          routePath = parsed.pathname + parsed.search + parsed.hash;
        } catch { /* rawUrl is already a relative path */ }
        console.log('[Deeplink] NAVIGATE_ROUTE ->', routePath);
        safePostMessage({ type: PostMessageType.NAVIGATE_ROUTE, payload: routePath });
        safePostMessage({ type: PostMessageType.CHAT_TOGGLE, isOpen: false });
        // Stop thinking after emitting event
        onThinkingStop?.();
      }, 100);
      return;
    }

    // For buttons with shortCut, disable immediately
    setIsActionTaken(true);
    setCountdown(null);

    if (onButtonClick) {
      onButtonClick(name, id, isHidden);
    }
  };

  const handlePaymentToggle = (index: number) => {
    if (
      isActionTaken ||
      !isLastMessage ||
      isFromHistory ||
      index === selectedPaymentIndex
    )
      return;

    const btn = paymentButtons[index];
    setSelectedPaymentIndex(index);

    // Check if button should be hidden
    const isHidden = checkIsHidden(btn.commandVariables);

    // Emit button click
    setIsActionTaken(true);
    setCountdown(null);
    if (onButtonClick) {
      onButtonClick(btn.name, btn.id, isHidden);
    }
  };

  const isButtonDisabled = isActionTaken || !isLastMessage || isFromHistory;

  const showCountdown = countdown !== null && countdown > 0 && !isActionTaken;

  const statusBlocks = sortedBlocks.filter((block) => {
    if (block.type !== "TEXT") return false;
    const textPayload = block.payload as TextBlockPayload;
    return !textPayload.isBold && !(textPayload.buttons && textPayload.buttons.length > 0);
  });

  const cardBlocks = sortedBlocks.filter((block) => {
    if (block.type === "ORDER_INFO") return true;
    if (block.type !== "TEXT") return false;
    const textPayload = block.payload as TextBlockPayload;
    return !!textPayload.isBold || !!(textPayload.buttons && textPayload.buttons.length > 0);
  });

  return (
    <div className="order-card-wrapper">
      {statusBlocks.map((block, idx) => {
        const textPayload = block.payload as TextBlockPayload;
        const statusText = textPayload.text?.toLowerCase().includes("thành công")
          ? `${textPayload.text.replace("!", "")}!`
          : textPayload.text;
        return (
          <div key={`status-${idx}`} className="order-header-status">
            {statusText}
          </div>
        );
      })}

      {/* Số giây cố định theo count_down (không giảm dần), chỉ label nút mới đếm ngược */}
      {showCountdown && (
        <div className="countdown-text">
          Đơn hàng sẽ tự động chuyển trạng thái <strong>Đã giao</strong> sau{" "}
          <strong>{initialCountdown} giây</strong>
        </div>
      )}

      <div className="order-card-container">
        {cardBlocks.map((block, idx) => {
          if (block.type === "TEXT") {
            const textPayload = block.payload as TextBlockPayload;
            if (textPayload.buttons && textPayload.buttons.length > 0) {
              return (
                <React.Fragment key={idx}>
                  {paymentMethod && (
                    <div className="order-info-row">
                      <span className="label">
                        {textPayload.text || "Thanh toán"}
                      </span>
                      <span className="value">{paymentMethod}</span>
                    </div>
                  )}

                  {paymentButtons.length >= 2 ? (
                    <div className="payment-toggle-container">
                      <span className="label">
                        {textPayload.text || "Thanh toán"}
                      </span>
                      <div className={`payment-toggle ${isButtonDisabled ? "disabled" : ""}`}>
                        {paymentButtons.map((btn, btnIdx) => (
                          <button
                            key={`payment-${btn.id ?? 'noid'}-${btnIdx}`}
                            className={`payment-toggle-item ${selectedPaymentIndex === btnIdx ? "active" : ""} ${isButtonDisabled ? "disabled" : ""}`}
                            onClick={() => handlePaymentToggle(btnIdx)}
                          >
                            {btn.name}
                          </button>
                        ))}
                        <div
                          className="payment-toggle-slider"
                          style={{
                            transform: `translateX(${selectedPaymentIndex * 100}%)`,
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    paymentButtons.length === 1 && (
                      <div className="payment-row">
                        <span className="label">
                          {textPayload.text || "Thanh toán"}
                        </span>
                        <span className="value">{paymentButtons[0].name}</span>
                      </div>
                    )
                  )}

                  <div className="divider"></div>
                  <div className="action-buttons">
                    {[...actionButtons]
                      .sort((a, b) => (a.intent === "cancel_order" ? -1 : 1))
                      .map((btn, btnIdx) => {
                        const isBusinessNoShortcut =
                          btn.type === "BUSINESS" && !btn.shortCut && btn.content;
                        const isCancel = btn.intent === "cancel_order";
                        const isInvoiceDetail = btn.intent === "invoice_detail";
                        const isBtnDisabled = isButtonDisabled || (isInvoiceDetail && selectedInvoice);

                        let finalStyle: any;

                        if (isCancel) {
                          finalStyle = {
                            width: 73,
                            height: 40,
                            padding: "10px 20px",
                            borderRadius: 999,
                            background: "#FFFFFF",
                            color: "#18181B",
                            border: "1px solid var(--button-outline-border-default, #D7D7DB)",
                          };
                        } else if (isInvoiceDetail) {
                          finalStyle = {
                            width: 338,
                            maxWidth: "100%",
                            height: 40,
                            padding: "10px 20px",
                            borderRadius: 999,
                            background: "#FFFFFF",
                            color: "var(--button-outline-text-default, #111114)",
                            border: "1px solid var(--button-outline-border-default, #D7D7DB)",
                            fontWeight: 500,
                            fontSize: 14,
                            lineHeight: "18px",
                          };
                        } else if (!isBusinessNoShortcut) {
                          finalStyle = {
                            width: 125,
                            height: 40,
                            padding: "10px 20px",
                            borderRadius: 999,
                            background: "linear-gradient(180deg, #EE0033 0%, #FF8D58 100%)",
                            color: "#FFFFFF",
                            border: "none",
                          };
                        } else {
                          let fallbackStyle: any = {
                            backgroundColor: "var(--colour-primary-light-xxl)",
                            color: "var(--colour-primary-shade-md)",
                            borderColor: "var(--colour-primary-light-xxl)",
                            borderStyle: "solid",
                            borderWidth: "1px",
                          };

                          const colorStyle = getButtonStyle(btn, isBtnDisabled);
                          finalStyle = Object.keys(colorStyle).length > 0
                            ? colorStyle
                            : fallbackStyle;
                        }

                        if (isBtnDisabled && isInvoiceDetail && selectedInvoice) {
                          finalStyle = { ...finalStyle, opacity: 0.5 };
                        }

                        const cleanName = btn.name.replace(/\s*\(OK\)$/i, "").trim();
                        const buttonLabel =
                          !isCancel && !isInvoiceDetail && showCountdown
                            ? `${cleanName.replace(/\s*\(\d+\)$/, "")} (${countdown})`
                            : cleanName;

                        return (
                          <button
                            key={`action-${btn.id ?? 'noid'}-${btnIdx}`}
                            onClick={() => {
                              if (isInvoiceDetail)
                                setSelectedInvoice(true);
                              handleButtonClick(btn.name, btn.id, btn);
                            }}
                            disabled={isButtonDisabled}
                            className={`action-btn ${isCancel ? "btn-cancel" : isInvoiceDetail ? "btn-invoice-detail" : "btn-primary"} ${isBusinessNoShortcut && !isInvoiceDetail ? "btn-full-width" : ""} ${isButtonDisabled ? "opacity-50 cursor-not-allowed disabled" : ""} ${isInvoiceDetail && selectedInvoice ? "disabled" : ""}`}
                            style={finalStyle}
                          >
                            {buttonLabel}
                          </button>
                        );
                      })}
                  </div>
                </React.Fragment>
              );
            }

            if (textPayload.isBold) {
              const rawTitle = textPayload.text || "";
              const titleText = /thông tin đơn hàng/i.test(rawTitle)
                ? "Đơn hàng"
                : rawTitle;
              const deliveredStatus = textPayload.status;

              return (
                <div key={idx} className="order-section-title">
                  <span>{titleText}</span>
                  {deliveredStatus && (
                    <span className="order-status-badge">{deliveredStatus}</span>
                  )}
                </div>
              );
            }
          }

          if (block.type === "ORDER_INFO") {
            const formatAmount = (value?: string) => {
              const amount = value || "0";
              return /đ$/i.test(amount.trim()) ? amount : `${amount}đ`;
            };

            if (products.length === 0) {
              return (
                <div key={idx} className="order-info-row">
                  <span className="label">Tổng tiền</span>
                  <span className="value">{formatAmount()}</span>
                </div>
              );
            }

            // Label lấy theo name của từng sản phẩm trong ORDER_INFO
            return (
              <React.Fragment key={idx}>
                {products.map((product, productIdx) => {
                  const name = product.name?.trim();
                  const quantity = parseNumber(product.quantity);
                  // quantity > 1 thì hiển thị kèm số lượng, ví dụ "5 gạo"
                  const label = !name
                    ? "Tổng tiền"
                    : quantity > 1
                      ? `${product.quantity.trim()} ${name}`
                      : name;

                  return (
                    <div key={`product-${productIdx}`} className="order-info-row">
                      <span className="label">{label}</span>
                      <span className="value">{formatAmount(product.totalAmount)}</span>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          }

          return null;
        })}
      </div>
    </div>
  );
};

export default OrderCard;
